import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import { http } from '@/shared/api/client';
import { setAppCurrency, setAppDateFormat } from '@/shared/lib/format';

import { PageTransition } from './PageTransition';
import { SelectSearchEnhancer } from './SelectSearchEnhancer';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';

export function AppShell() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  // Load the store's configured currency once and apply it app-wide so money is
  // formatted in the chosen currency everywhere. The query state change re-renders
  // the Outlet subtree, so amounts reformat as soon as the currency resolves.
  const appPrefs = useQuery<{ code: string; symbol: string; date_format?: string }>({
    queryKey: ['app', 'currency'],
    queryFn: async () => {
      const { data } = await http.get('/api/app_currency');
      // Apply INSIDE the fetch (before React re-renders with the new data):
      // an effect would run after the children already rendered with the
      // stale module-level value, leaving old symbols on screen.
      setAppCurrency(data?.code);
      setAppDateFormat(data?.date_format ?? '');
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });

  // Close the mobile drawer whenever the route changes.
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Lock body scroll while the mobile drawer is open.
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  return (
    <div className="flex h-screen w-screen overflow-hidden">
      <SelectSearchEnhancer />

      {/* Backdrop overlay (mobile/tablet only) */}
      {mobileOpen && (
        <button
          type="button"
          aria-label="Close menu"
          onClick={() => setMobileOpen(false)}
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden"
        />
      )}

      <Sidebar
        collapsed={collapsed}
        onToggle={() => setCollapsed((c) => !c)}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />

      <div className="admin-content flex min-w-0 flex-1 flex-col">
        <Topbar onMenuClick={() => setMobileOpen(true)} />
        <main className="flex-1 overflow-y-auto bg-background p-4 sm:p-5 lg:p-6">
          <PageTransition
            key={`${appPrefs.data?.code ?? ''}-${appPrefs.data?.date_format ?? ''}`}
          >
            <Outlet />
          </PageTransition>
        </main>
      </div>
    </div>
  );
}
